// La conversación entera: lista de mensajes + barra de entrada.
//
// ES EL COMPONENTE COMPARTIDO. El panel lateral y la pantalla dedicada montan
// este mismo árbol y solo le cambian el ancho de la columna y los márgenes:
// las burbujas, el indicador de "pensando", el estado vacío y la barra de
// entrada están escritos UNA vez.

import { useEffect, useRef } from 'react';
import { useChatAtlas } from '../ChatAtlasContext';
import { BarraEntradaChat } from './BarraEntradaChat';
import { BurbujaMensaje, type VistaGrafico } from './BurbujaMensaje';

/** Puntitos de "está escribiendo". Copiado de `ChatPanelV2`. */
const Puntitos = () => (
  <div className="flex items-center gap-1.5 py-1" aria-label="Atlas está escribiendo">
    {[0, 150, 300].map((retraso) => (
      <span
        key={retraso}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#02062B]/[0.3]"
        style={{ animationDelay: `${retraso}ms` }}
      />
    ))}
  </div>
);

/** Etiqueta con shimmer para cuando corre una herramienta del servidor
 *  (búsqueda web, scrapers): esas tardan y sin esto el chat parece colgado. */
const Actividad: React.FC<{ etiqueta: string }> = ({ etiqueta }) => (
  <div className="flex items-center gap-2 py-1" role="status">
    <style>{`@keyframes chatAtlasShimmer { to { background-position: -200% 0 } }`}</style>
    <span
      className="bg-clip-text text-[15px] font-medium text-transparent"
      style={{
        backgroundImage:
          'linear-gradient(90deg, rgba(2,6,43,0.35) 25%, rgba(2,6,43,0.85) 50%, rgba(2,6,43,0.35) 75%)',
        backgroundSize: '200% 100%',
        animation: 'chatAtlasShimmer 1.4s linear infinite',
      }}
    >
      {etiqueta}
    </span>
  </div>
);

const ARRANQUES = [
  '¿Qué le falta a mi marca para vender más?',
  'Dame tres ideas de contenido para esta semana',
  'Analizá a mi competencia',
];

interface Props {
  /** Ancho de la columna de mensajes. La pantalla dedicada centra en una
   *  columna cómoda; el panel usa todo el ancho de la franja. */
  anchoColumna?: string;
  /** Padding lateral. */
  padding?: string;
  /** Qué hace la tarjeta de una respuesta gráfica. En el panel lateral no hay
   *  ancho para dibujarla, así que manda a la pantalla dedicada. */
  vistaGrafico?: VistaGrafico;
  /** Se llama con el id del gráfico al tocar su tarjeta. Lo resuelve cada
   *  contenedor: la pantalla lo abre a la derecha, el panel navega a `/chat`. */
  onAbrirGrafico?: (id: string) => void;
}

export const ConversacionChatAtlas: React.FC<Props> = ({
  anchoColumna = 'max-w-3xl',
  padding = 'px-4 sm:px-6',
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
}) => {
  const chat = useChatAtlas();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll al fondo cuando llega contenido nuevo, PERO solo si el usuario
  // ya estaba cerca del fondo: si está leyendo algo de más arriba, arrastrarlo
  // hacia abajo en cada token es insoportable.
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const distancia = sc.scrollHeight - sc.scrollTop - sc.clientHeight;
    if (distancia < 140) sc.scrollTo({ top: sc.scrollHeight, behavior: 'smooth' });
  }, [chat.mensajes, chat.actividad]);

  const ultimo = chat.mensajes[chat.mensajes.length - 1];
  const esperandoPrimerToken =
    chat.streaming && (!ultimo || ultimo.rol === 'user' || !ultimo.texto);
  const vacio = chat.mensajes.length === 0;

  return (
    <>
      <div
        ref={scrollRef}
        // `overscroll-contain`: llegado al final, la rueda NO sigue scrolleando
        // la pantalla de atrás. Es lo que reemplaza a bloquear el scroll del
        // body — que en desktop no serviría igual (scrollea
        // `.content-panel-clip`, no el body) y que además dejaría la página de
        // atrás trabada si el panel se cerrara de una forma no prevista.
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${padding} py-6`}
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className={`mx-auto flex flex-col gap-5 ${anchoColumna}`}>
          {vacio ? (
            <div className="flex flex-col items-start gap-4 py-6">
              <div>
                <p className="text-[20px] font-semibold text-brand-black">
                  Hablemos de tu marca
                </p>
                <p className="mt-1 text-[14px] text-[#02062B]/[0.55]">
                  {chat.contexto?.pantalla
                    ? `Estás en ${chat.contexto.pantalla}. Preguntame lo que quieras sobre eso o sobre tu negocio.`
                    : 'Preguntame sobre tu negocio, tu contenido o qué conviene hacer esta semana.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {ARRANQUES.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => chat.enviarMensaje(a)}
                    className="rounded-full border border-[#02062B]/[0.1] bg-white py-2 px-4 text-left text-[13px] font-medium text-brand-black transition-colors hover:border-[#1644EB]/[0.35] hover:text-brand-primary"
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            chat.mensajes.map((m) => (
              <BurbujaMensaje
                key={m.id}
                mensaje={m}
                bloqueado={chat.streaming}
                onSugerencia={(texto) => chat.enviarMensaje(texto)}
                vistaGrafico={vistaGrafico}
                onAbrirGrafico={onAbrirGrafico}
                graficoActivoId={chat.graficoActivoId}
              />
            ))
          )}

          {chat.actividad ? (
            <Actividad etiqueta={chat.actividad} />
          ) : esperandoPrimerToken ? (
            <Puntitos />
          ) : null}
        </div>
      </div>

      <div
        className={`shrink-0 ${padding} pt-3`}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
      >
        <div className={`mx-auto ${anchoColumna}`}>
          <BarraEntradaChat
            valor={chat.borrador}
            onCambiarValor={chat.setBorrador}
            onEnviar={chat.enviarMensaje}
            streaming={chat.streaming}
            onDetener={chat.detener}
            interruptores={chat.interruptores}
            onCambiarInterruptor={chat.setInterruptor}
            adjuntos={chat.adjuntos}
            onCambiarAdjuntos={chat.setAdjuntos}
            autoFoco
          />
        </div>
      </div>
    </>
  );
};

export default ConversacionChatAtlas;
